import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';

import { Button } from '#/modules/commonComponents/button';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import MtrPickerDialog from '#/modules/imsMgmt/settings/mtr/components/MtrPickerDialog';

/**
 * BOM 表單：關聯物料（單選；可清除）。實際綁定於儲存後 PATCH mtr。
 */
const BomMtrLinkSection = ({ control, setValue, disabled = false }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);

  const linkedMtrId = useWatch({ control, name: 'linkedMtrId', defaultValue: '' });
  const linkedMtrNo = useWatch({ control, name: 'linkedMtrNo', defaultValue: '' });
  const linkedMtrName = useWatch({ control, name: 'linkedMtrName', defaultValue: '' });

  const initialSelectedRows = useMemo(() => {
    if (!linkedMtrId) {
      return [];
    }
    return [{
      optionMtrId: linkedMtrId,
      mtrNo: linkedMtrNo,
      name: linkedMtrName,
    }];
  }, [linkedMtrId, linkedMtrNo, linkedMtrName]);

  function handleOpenPicker() {
    setPickerKey((prev) => prev + 1);
    setPickerOpen(true);
  }

  function handleClear() {
    setValue('linkedMtrId', '', { shouldDirty: true });
    setValue('linkedMtrNo', '', { shouldDirty: true });
    setValue('linkedMtrName', '', { shouldDirty: true });
  }

  function handlePickerConfirm(selectedRows) {
    const row = Array.isArray(selectedRows) ? selectedRows[0] : null;
    const id = row?.optionMtrId ?? row?.id;
    if (!id) {
      return;
    }
    setValue('linkedMtrId', id, { shouldDirty: true });
    setValue('linkedMtrNo', row.mtrNo ?? '', { shouldDirty: true });
    setValue('linkedMtrName', row.name ?? '', { shouldDirty: true });
  }

  return (
    <section className="relative w-full">
      <div className="form-section__header">
        <h2 className="form-block__title mb-0! mr-2">關聯物料</h2>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            disabled={disabled}
            onClick={handleOpenPicker}
          >
            選擇物料
          </Button>
          {linkedMtrId ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={handleClear}
            >
              清除關聯
            </Button>
          ) : null}
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        一對一；儲存時先寫 BOM，再以物料
        {' '}
        <code className="text-xs">bomId</code>
        {' '}
        PATCH 綁定（不寫進 BOM body）。
      </p>
      <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
        {linkedMtrId ? (
          <MaterialNameStack
            name={linkedMtrName}
            code={linkedMtrNo}
          />
        ) : (
          <p className="text-sm text-muted-foreground">尚未關聯物料</p>
        )}
      </div>

      <MtrPickerDialog
        key={pickerKey}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialSelectedRows={initialSelectedRows}
        onConfirm={handlePickerConfirm}
        enableMultiSelection={false}
        includeMtrKind={false}
        title="請選擇關聯物料"
        description="單選物料；確認後於儲存 BOM 時寫入關聯。"
      />
    </section>
  );
};

BomMtrLinkSection.propTypes = {
  control: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default BomMtrLinkSection;
